/** Suspend / resume / terminate a partner, with an optional reason (audited).
 *  Says what each step does to keys and the sandbox before the click. */
import { Alert, Button, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { lifecycle, type LifecycleStep } from '@/api/partners';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';

import { partnerErrorMessage } from './partnerErrors';

const COPY: Record<LifecycleStep, { title: string; button: string; effect: string; done: string }> = {
  suspend: {
    title: 'Suspend partner',
    button: 'Suspend',
    effect:
      'Its developer keys are denied and its sandbox is suspended, until the partner is resumed. Nothing is deleted or revoked.',
    done: 'Partner suspended.',
  },
  resume: {
    title: 'Resume partner',
    button: 'Resume',
    effect:
      'Its developer keys are accepted again and the sandbox suspension this partner suspension set is lifted. A suspension set separately stays.',
    done: 'Partner resumed.',
  },
  terminate: {
    title: 'Terminate partner',
    button: 'Terminate',
    effect:
      'Final: every developer key of the partner is revoked, and a terminated partner cannot be resumed. Its sandbox, which accepts only the partner’s keys, can no longer be called.',
    done: 'Partner terminated.',
  },
};

interface Props {
  partnerId: number;
  partnerName: string;
  step: LifecycleStep | null;
  onClose: () => void;
}

export function PartnerLifecycleDialog({ partnerId, partnerName, step, onClose }: Props) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const { register, reset, handleSubmit } = useForm<{ reason: string }>({ defaultValues: { reason: '' } });
  useEffect(() => {
    if (step) reset({ reason: '' });
  }, [step, reset]);

  const mutation = useMutation({
    mutationFn: ({ s, reason }: { s: LifecycleStep; reason: string }) => lifecycle(partnerId, s, reason.trim()),
    onSuccess: (_, { s }) => {
      snackbar.show(COPY[s].done, 'success');
      void qc.invalidateQueries({ queryKey: ['partners'] });
      onClose();
    },
    onError: (e) => snackbar.show(partnerErrorMessage(e, 'The change failed.'), 'error'),
  });

  if (!step) return null;
  const copy = COPY[step];
  return (
    <AppDialog
      open
      onClose={onClose}
      title={`${copy.title} — ${partnerName}`}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            color={step === 'resume' ? 'primary' : 'error'}
            disabled={mutation.isPending}
            onClick={handleSubmit((v) => mutation.mutate({ s: step, reason: v.reason }))}
          >
            {copy.button}
          </Button>
        </>
      }
    >
      <Alert severity={step === 'terminate' ? 'warning' : 'info'} sx={{ mb: 2 }}>
        {copy.effect}
      </Alert>
      <TextField
        label="Reason"
        fullWidth
        multiline
        minRows={2}
        {...register('reason')}
        helperText="Kept with the partner and in the audit log."
        inputProps={{ maxLength: 2000 }}
      />
    </AppDialog>
  );
}
